import { pollVisible } from '../api/poll.js';
/**
 * Таблиця рапортів на звільнення: хто якого дня йде за рапортом.
 *
 * Рапорт — не позначка харчування, а запис на дату з часом і походженням
 * (діловод чи бот), тож живе він у своїй таблиці бази, а сітка лише показує
 * місяць тим самим виглядом, що й наряди. Позначки дві: «р» — звичайний
 * рапорт, «д» — добовий (до ранку; за сніданок після нього компенсацію ставить
 * «Перенесення»). Складений у боті видно з підказки. Клік обраною позначкою
 * ставить рапорт «за розпорядком», знімає його або міняє вид; свій час
 * вписується на сторінці рапортів.
 */

import { isWeekend, todayIso, weekdayLong, weekdayShort, type IsoDate, type MealPattern, type Person, type Position, type Rank, type Unit } from '@kancelaria/shared';
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { useRuler } from './useRuler.js';
import { api, errorText, isOfflineQueued } from '../api/client.js';
import type { ViewOptions } from '../pages/TablePage.js';
import { toast } from '../store/ui.js';
import { FillRow, useFillRow } from './Grid.js';

interface LeaveReport {
  personId: string;
  date: IsoDate;
  /** «15:15-07:30» або порожньо — за розпорядком дня. */
  span: string;
  source: 'clerk' | 'bot';
  kind: ReportKind;
}

/** Звичайний рапорт на звільнення чи добовий. */
export type ReportKind = 'leave' | 'duty';

const keyOf = (personId: string, date: IsoDate): string => `${personId}|${date}`;

/**
 * Вигляд позначок рапорту. Звичайний — акцентом курсу, добовий — синім, щоб
 * відрізнявся з першого погляду. Тими ж кольорами залиті кнопки над таблицею.
 */
export const REPORT_KINDS: Record<ReportKind, { letter: string; label: string; bg: string; fg: string }> = {
  leave: {
    letter: 'р',
    label: 'Рапорт',
    bg: 'color-mix(in srgb, var(--accent) 26%, transparent)',
    fg: 'var(--accent)',
  },
  duty: {
    letter: 'д',
    label: 'Рапорт до ранку',
    bg: 'color-mix(in srgb, #3b82f6 26%, transparent)',
    fg: '#5b9cf6',
  },
};

const markedStyle = (kind: ReportKind): CSSProperties => ({
  ['--mk-bg' as string]: REPORT_KINDS[kind].bg,
  ['--mk-fg' as string]: REPORT_KINDS[kind].fg,
});

export function LeaveReportsGrid(props: {
  people: Person[];
  dates: IsoDate[];
  ranks: Rank[];
  units: Unit[];
  positions: Position[];
  patterns: MealPattern[];
  locale: string;
  view: ViewOptions;
  readOnly: boolean;
  /** Спроба поставити рапорт, коли таблиця заблокована, — щоб пояснити чому. */
  onLocked?: () => void;
  /** Яку позначку ставить клік: обрана кнопка над таблицею. */
  tool: ReportKind;
}): ReactNode {
  const { people, dates, view } = props;
  const from = dates[0];
  const to = dates[dates.length - 1];
  const [reports, setReports] = useState<Map<string, LeaveReport>>(new Map());
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    if (!from || !to) return;
    try {
      const res = await api.get<{ reports: LeaveReport[] }>(`/api/leave-reports/range?from=${from}&to=${to}`);
      setReports(new Map(res.reports.map(r => [keyOf(r.personId, r.date), r])));
    } catch (e) {
      toast(errorText(e), 'err');
    }
  }, [from, to]);

  useEffect(() => {
    // Рапорти подають у боті й видаляють у «Рапортах» — перечитуємо, поки
    // таблиця відкрита, інакше позначка висіла б до перезаходу.
    return pollVisible(load, 30_000);
  }, [load]);

  /**
   * Клік обраною позначкою: порожньо — ставимо рапорт за розпорядком, той
   * самий вид — знімаємо, інший — міняємо вид, час лишається. Відповідь —
   * увесь день, ним і оновлюємось.
   */
  const toggle = async (person: Person, date: IsoDate): Promise<void> => {
    if (props.readOnly) {
      props.onLocked?.();
      return;
    }
    if (busy) return;
    const key = keyOf(person.id, date);
    const had = reports.get(key);
    const off = had?.kind === props.tool;
    setBusy(key);
    try {
      const res = await api.put<{ date: IsoDate; reports: LeaveReport[] }>('/api/leave-reports', {
        date,
        personId: person.id,
        on: !off,
        kind: props.tool,
        span: had?.span ?? '',
      });
      setReports(prev => {
        const next = new Map([...prev].filter(([, r]) => r.date !== res.date));
        for (const r of res.reports) next.set(keyOf(r.personId, r.date), r);
        return next;
      });
      if (off && had?.source === 'bot') toast(`Знято рапорт, який ${person.lastName} склав у боті`, 'ok');
    } catch (e) {
      if (isOfflineQueued(e)) {
        toast('Збережено локально — надішлеться, коли з’явиться сервер', 'ok');
        void load();
      } else toast(errorText(e), 'err');
    } finally {
      setBusy(null);
    }
  };

  const today = todayIso();
  const fillRef = useFillRow();
  const days = useMemo(
    () =>
      dates.map(d => ({
        date: d,
        n: Number(d.slice(8)),
        weekend: isWeekend(d),
        today: d === today,
        short: weekdayShort(d, props.locale),
        long: weekdayLong(d, props.locale),
      })),
    [dates, today, props.locale],
  );

  const perDay = days.map(day => people.filter(p => reports.has(keyOf(p.id, day.date))).length);
  // Та сама лінійка під курсором, що й у решті таблиць місяця.
  const { wrapRef, colRef, rowRef, tableRef, headRef, footRef, moveRuler } = useRuler(view.crosshair, people.length);
  const total = perDay.reduce((a, b) => a + b, 0);

  return (
    <div className="grid-wrap" ref={wrapRef} onMouseLeave={() => moveRuler(null, null)}>
      <div className="ruler-col" ref={colRef} />
      <div className="ruler-row" ref={rowRef} />
      <table
        ref={tableRef}
        className={`grid duty${view.crosshair ? ' ruler' : ''}${view.dayLines ? ' daylines' : ''}`}
        style={
          {
            '--l-rank': `${view.numbers ? 34 : 0}px`,
            '--l-name': `${(view.numbers ? 34 : 0) + (view.rank ? 84 : 0)}px`,
          } as CSSProperties
        }
      >
        <thead ref={headRef}>
          <tr>
            {view.numbers ? <th className="sticky c-num">№</th> : null}
            {view.rank ? <th className="sticky c-rank">Звання</th> : null}
            <th className="sticky c-name">Прізвище, ім’я та по батькові</th>
            {view.unit ? <th className="sticky c-grp">Група</th> : null}
            {days.map(day => (
              <th
                key={day.date}
                className={`day dsep${day.weekend ? ' we' : ''}${day.today ? ' today' : ''}`}
                data-day={day.date}
                title={`${day.n} число`}
              >
                <div className="n">{day.n}</div>
                <div className="w">{day.short}</div>
              </th>
            ))}
            {view.totals ? (
              <th className="c-sum grp wide" style={{ right: 0 }} title="Скільки рапортів за показаний період">
                Рапортів
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {people.map((p, i) => {
            const rank = props.ranks.find(r => r.code === p.rankCode);
            const unit = props.units.find(u => u.id === p.unitId);
            const position = props.positions.find(x => x.code === p.positionCode && x.short);
            const pattern = props.patterns.find(mp => mp.id === p.patternId);
            let count = 0;
            return (
              <tr key={p.id}>
                {view.numbers ? <td className="sticky c-num">{i + 1}</td> : null}
                {view.rank ? <td className="sticky c-rank">{rank?.short || rank?.name || ''}</td> : null}
                <td className="sticky c-name" title={[rank?.name, p.lastName, unit?.name].filter(Boolean).join(' • ')}>
                  <span className="nm">
                    {p.lastName} {p.firstName ? p.firstName.charAt(0) + '.' : ''}
                    {p.patronymic ? p.patronymic.charAt(0) + '.' : ''}
                  </span>
                  <span className="dots">
                    <i
                      className={`dot-flag home${pattern && !pattern.isDefault ? ' on' : ''}`}
                      title={pattern?.name ?? 'категорія'}
                    />
                    <i className={`dot-flag cmd${position ? ' on' : ''}`} title={position?.name ?? 'посада'} />
                  </span>
                </td>
                {view.unit ? <td className="sticky c-grp">{unit?.name ?? ''}</td> : null}
                {days.map(day => {
                  const r = reports.get(keyOf(p.id, day.date));
                  if (r) count++;
                  const label = r ? REPORT_KINDS[r.kind].label.toLowerCase() : '';
                  const how = r ? (r.span ? `${label} ${r.span}` : `${label}, за розпорядком`) : '';
                  return (
                    <td
                      key={day.date}
                      className={['cell', 'dsep', day.weekend ? 'we' : '', r ? 'marked' : ''].filter(Boolean).join(' ')}
                      style={r ? markedStyle(r.kind) : undefined}
                      data-d={day.date}
                      data-letter={r ? REPORT_KINDS[r.kind].letter : ''}
                      title={`${day.n} ${day.long}${r ? ` — ${how}${r.source === 'bot' ? ', з бота' : ''}` : ''}`}
                      onPointerEnter={e => moveRuler(e.currentTarget, day.date)}
                      onClick={() => void toggle(p, day.date)}
                    />
                  );
                })}
                {view.totals ? (
                  <td className="c-sum grp wide" style={{ right: 0 }}>
                    {count || ''}
                  </td>
                ) : null}
              </tr>
            );
          })}
          <FillRow
            fillRef={fillRef}
            view={view}
            days={days}
            perDay={1}
            empty={!people.length}
            tail={view.totals ? <td className="c-sum grp wide" style={{ right: 0 }} /> : null}
          />
        </tbody>
        {view.footer ? (
          <tfoot ref={footRef}>
            <tr>
              {view.numbers ? <td className="sticky c-num" /> : null}
              {view.rank ? <td className="sticky c-rank" /> : null}
              <td className="sticky c-name">За рапортом</td>
              {view.unit ? <td className="sticky c-grp" /> : null}
              {days.map((day, i) => (
                <td key={day.date} className="dsep" data-d={day.date}>
                  {perDay[i] || ''}
                </td>
              ))}
              {view.totals ? (
                <td className="c-sum grp wide" style={{ right: 0 }}>
                  {total || ''}
                </td>
              ) : null}
            </tr>
          </tfoot>
        ) : null}
      </table>
    </div>
  );
}
